import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, MapPin, ArrowRight, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { HAR_MARKET_GROUPS, HOUSTON_SUB_MARKETS, HOUSTON_SUB_MARKET_ZIPS } from "../lib/harMarketAreas";
import Seo from "../components/Seo";
import IdxDisclaimer from "../components/IdxDisclaimer";
import SaveHeart from "../components/SaveHeart";
import HoverCyclePhoto from "../components/HoverCyclePhoto";

const TYPES = [
  "All",
  "Single Family Residence",
  "Condominium",
  "Mid/Hi-Rise Condominium",
  "Townhouse",
  "Duplex",
  "Multi-Family",
  "Country Homes/Acreage",
  "Residential Lots & Land",
];
const BEDS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const PAGE_SIZE = 24;

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

export default function MlsListings() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [city, setCity] = useState(sp.get("city") || "All");
  const [ptype, setPtype] = useState(sp.get("property_type") || "All");
  const [minPrice, setMinPrice] = useState(sp.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(sp.get("max_price") || "");
  const [beds, setBeds] = useState(Number(sp.get("beds") || 0));
  const [q, setQ] = useState(sp.get("q") || "");

  const fetchList = async (append = false) => {
    setLoading(true);
    const params = { limit: PAGE_SIZE, skip: append ? page * PAGE_SIZE : 0 };
    // Houston sub-markets live inside city="Houston" — map to known zip codes.
    if (city !== "All") {
      if (HOUSTON_SUB_MARKETS.has(city)) {
        const zips = HOUSTON_SUB_MARKET_ZIPS[city];
        if (zips?.length) {
          params.zip_codes = zips.join(",");
        } else {
          params.q = q ? `${q} ${city}` : city;
        }
        if (q && !params.q) params.q = q;
      } else {
        params.city = city;
        if (q) params.q = q;
      }
    } else if (q) {
      params.q = q;
    }
    if (ptype !== "All") params.property_type = ptype;
    if (minPrice) params.min_price = Number(minPrice);
    if (maxPrice) params.max_price = Number(maxPrice);
    if (beds > 0) params.beds = beds;

    try {
      const { data } = await api.get("/mls/listings", { params });
      setTotal(data.total);
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    } catch (e) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Persist filters to URL
    const next = new URLSearchParams();
    if (city && city !== "Houston") next.set("city", city);
    if (ptype && ptype !== "All") next.set("property_type", ptype);
    if (minPrice) next.set("min_price", minPrice);
    if (maxPrice) next.set("max_price", maxPrice);
    if (beds > 0) next.set("beds", String(beds));
    if (q) next.set("q", q);
    setSp(next, { replace: true });
    setPage(0);
    fetchList(false);
  }, [city, ptype, minPrice, maxPrice, beds, q]);

  const hasMore = items.length < total;

  const loadMore = async () => {
    setPage((p) => p + 1);
    await fetchList(true);
  };

  const reset = () => {
    setCity("Houston"); setPtype("All"); setMinPrice(""); setMaxPrice(""); setBeds(0); setQ("");
  };

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="mls-listings-page">
      <Seo
        title="Houston MLS Listings — Live HAR Feed"
        description="Live Houston MLS listings powered by CoreLogic Trestle. Browse active homes for sale across Houston, Katy, Sugar Land, The Woodlands, Cypress and more."
        path="/mls-listings"
      />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">Live HAR Feed · Powered by Trestle</div>
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-[#1A1A1A]">
              Every home in Houston, <em className="not-italic text-[#E63946]">right now</em>.
            </h1>
            <p className="text-[#555] mt-5 max-w-2xl text-lg">
              Real-time listings from the Houston Association of REALTORS® MLS. Updated nightly.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/mls-map"
              className="bg-[#1A1A1A] hover:bg-[#333] text-white px-5 py-2.5 rounded-md text-sm font-semibold flex items-center gap-2"
              data-testid="mls-view-map-cta"
            >
              <MapPin className="w-4 h-4" /> View on Map
            </Link>
          </div>
        </div>
      </section>

      {/* Filters + Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search address, city, or ZIP…"
            className="flex-1 min-w-[220px] px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E63946]"
            data-testid="mls-search-input"
          />
          <select value={city} onChange={(e) => setCity(e.target.value)} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-city-filter">
            <option value="All">All Houston Metro</option>
            {HAR_MARKET_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <select value={beds} onChange={(e) => setBeds(Number(e.target.value))} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-beds-filter">
            {BEDS.map((b) => <option key={b} value={b}>{b === 0 ? "Any beds" : b === 9 ? "9+ beds" : `${b}+ beds`}</option>)}
          </select>
          <button onClick={() => setFiltersOpen((v) => !v)} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm flex items-center gap-1" data-testid="mls-more-filters">
            <SlidersHorizontal className="w-3.5 h-3.5" /> {filtersOpen ? "Hide filters" : "More filters"}
          </button>
          {(city !== "Houston" || ptype !== "All" || minPrice || maxPrice || beds || q) && (
            <button onClick={reset} className="text-xs text-gray-500 hover:text-[#E63946] flex items-center gap-1" data-testid="mls-reset-filters">
              <X className="w-3 h-3" /> Reset
            </button>
          )}
        </div>

        {filtersOpen && (
          <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <select value={ptype} onChange={(e) => setPtype(e.target.value)} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-ptype-filter">
              {TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <input type="number" placeholder="Min price" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-minprice" />
            <input type="number" placeholder="Max price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="mls-maxprice" />
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-gray-500">
            {loading && items.length === 0 ? "Loading…" : `${total.toLocaleString()} listings match`}
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="text-center bg-white border border-dashed border-gray-200 rounded-xl py-16">
            <div className="font-display text-2xl text-gray-500 mb-2">No listings match your filters</div>
            <button onClick={reset} className="text-[#E63946] hover:underline text-sm font-semibold">Reset filters</button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((l) => (
            <Link
              key={l.listing_key}
              to={`/mls-listings/${l.listing_key}`}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group"
              data-testid={`mls-card-${l.listing_key}`}
            >
              <div className="relative aspect-[4/3] bg-stone-100">
                <HoverCyclePhoto photos={l.photos || []} alt={l.address} className="w-full h-full object-cover" />
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-semibold">
                  {l.status}
                </div>
                <SaveHeart
                  listingKey={l.listing_key}
                  address={l.address}
                  className="absolute top-3 right-3"
                />
              </div>
              <div className="p-5">
                <div className="font-display text-2xl font-semibold">{fmt(l.price)}</div>
                <div className="text-sm text-[#1A1A1A] mt-1">{l.address}</div>
                <div className="text-xs text-gray-500">{l.city}, {l.state} {l.zip || ""}</div>
                <div className="text-xs text-gray-600 mt-2">
                  {l.beds} bd · {l.baths} ba · {l.sqft?.toLocaleString?.() || l.sqft} sqft
                </div>
                <div className="text-[10px] text-gray-400 mt-2 truncate">
                  {l.list_office} · MLS #{l.listing_key}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {hasMore && (
          <div className="mt-10 flex justify-center">
            <button
              onClick={loadMore}
              disabled={loading}
              className="bg-[#1A1A1A] hover:bg-[#333] text-white px-8 py-3 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
              data-testid="mls-load-more"
            >
              {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Loading…</> : <>Load more <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        )}

        <div className="mt-16 text-[10px] uppercase tracking-[0.2em] text-gray-400 text-center leading-relaxed">
          IDX data © Houston Association of REALTORS® · Delivered via CoreLogic Trestle · Refreshed nightly
        </div>
        <IdxDisclaimer />
      </section>
    </div>
  );
}
